import React, { useState, useEffect } from 'react';
import { X, Share, Plus, Download, Smartphone } from 'lucide-react';

const InstallPrompt = () => {
  const [show, setShow] = useState(false);
  const [platform, setPlatform] = useState('unknown'); // ios, android, desktop
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    // Don't show if already installed or dismissed recently
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedAt = parseInt(dismissed, 10);
      // Don't show again for 7 days
      if (Date.now() - dismissedAt < 7 * 24 * 60 * 60 * 1000) return;
    }

    // Check if already in standalone mode (already installed)
    if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone) return;

    // Detect platform
    const ua = navigator.userAgent || '';
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/.test(ua);

    if (isIOS) {
      setPlatform('ios');
      // Show after short delay
      const timer = setTimeout(() => setShow(true), 2000);
      return () => clearTimeout(timer);
    } else if (isAndroid) {
      setPlatform('android');
    } else {
      setPlatform('desktop');
    }

    // Listen for beforeinstallprompt (Android/Chrome)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => setShow(true), 2000);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // For non-iOS, show generic prompt after delay if no native prompt
    if (!isIOS) {
      const timer = setTimeout(() => setShow(true), 3000);
      return () => { clearTimeout(timer); window.removeEventListener('beforeinstallprompt', handler); };
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const result = await deferredPrompt.userChoice;
      if (result.outcome === 'accepted') {
        setShow(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  if (!show) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-[9998] animate-in slide-in-from-bottom duration-500" data-testid="pwa-install-prompt">
      <div className="max-w-md mx-auto bg-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow">
              <img src="/logo-fisl.png" alt="FISL" className="w-8 h-8 object-contain" />
            </div>
            <div>
              <p className="text-white font-bold text-sm">FISLApp</p>
              <p className="text-slate-400 text-xs">Installer l'application</p>
            </div>
          </div>
          <button onClick={handleDismiss} className="text-slate-500 hover:text-slate-300 p-1" data-testid="pwa-dismiss-btn">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-4 pb-4">
          {platform === 'ios' ? (
            <div className="space-y-3">
              <p className="text-slate-300 text-sm">
                Ajoutez FISLApp sur votre ecran d'accueil pour un acces rapide :
              </p>
              <div className="flex items-center gap-3 bg-slate-800 rounded-xl p-3">
                <div className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Share className="h-4 w-4 text-blue-400" />
                </div>
                <p className="text-slate-300 text-xs">
                  <span className="font-semibold text-white">1.</span> Appuyez sur le bouton <span className="font-semibold text-blue-400">Partager</span> en bas de Safari
                </p>
              </div>
              <div className="flex items-center gap-3 bg-slate-800 rounded-xl p-3">
                <div className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Plus className="h-4 w-4 text-green-400" />
                </div>
                <p className="text-slate-300 text-xs">
                  <span className="font-semibold text-white">2.</span> Choisissez <span className="font-semibold text-green-400">Sur l'ecran d'accueil</span>
                </p>
              </div>
            </div>
          ) : platform === 'android' && deferredPrompt ? (
            <div className="space-y-3">
              <p className="text-slate-300 text-sm">
                Installez FISLApp pour un acces rapide depuis votre ecran d'accueil.
              </p>
              <button onClick={handleInstall} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-xl text-sm flex items-center justify-center gap-2 transition-colors" data-testid="pwa-install-btn">
                <Download className="h-4 w-4" /> Installer l'application
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-slate-300 text-sm">
                Ajoutez FISLApp a votre ecran d'accueil pour un acces rapide.
              </p>
              <div className="flex items-center gap-3 bg-slate-800 rounded-xl p-3">
                <div className="w-8 h-8 bg-violet-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Smartphone className="h-4 w-4 text-violet-400" />
                </div>
                <p className="text-slate-300 text-xs">
                  Sur mobile, ouvrez dans <span className="font-semibold text-violet-400">Safari</span> (iPhone) ou <span className="font-semibold text-violet-400">Chrome</span> (Android) et suivez les instructions.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
